import type { OptionType } from '~/features/types';
import { specialtiesToOptions } from '~/features/vault/aoi/utils';
import type { AreaOfInterest } from '~/types/common/aoi';
import type { Provider, ProviderCategory } from '~/types/mcv/provider';
import { getData } from '~/utils/service';
import {
  EMPTY_PROVIDER_JOB_SEARCH_FILTERS,
  type ProviderJobSearchFilterValues,
} from './providerJobSearchFilters.schema';

const sameName = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * The provider's own category and specialty, as the job search's starting filters
 * (MCV-1822). Returns null when the provider has no category, so the search opens unfiltered
 * exactly as it did before.
 *
 * The category comes from the provider record. The specialty is that of the provider's area of
 * interest whose specialty is one of the category's specialties: the areas-of-interest API
 * returns the specialty by name only, so it is matched by name against the same specialty
 * options the filter's own Specialty select offers. It is seeded only when the matching areas of
 * interest all sit in one specialty; with several specialties, any pick would be arbitrary, so the
 * category alone is seeded.
 * A failed or empty areas-of-interest lookup also gives the category default.
 *
 * `signal` is forwarded to every request, so a caller that gives up can cancel them all.
 */
export const loadProviderJobSearchDefaults = async (
  signal?: AbortSignal
): Promise<ProviderJobSearchFilterValues | null> => {
  const provider = await getData<Provider>('currentProvider', { signal });
  const categoryId = provider?.category?.id ?? provider?.type?.providerCategoryId;
  if (typeof categoryId !== 'number') return null;

  const categoryLabel =
    provider.category?.providerCategoryCode ??
    (await getData<ProviderCategory[]>('providerCategories', { signal })).find(
      (category) => category.id === categoryId
    )?.providerCategoryCode;
  if (!categoryLabel) return null;

  const defaults: ProviderJobSearchFilterValues = {
    ...EMPTY_PROVIDER_JOB_SEARCH_FILTERS,
    providerCategory: { value: categoryId, label: categoryLabel },
  };

  try {
    const [aois, specialtyOptions] = await Promise.all([
      getData<AreaOfInterest[]>('aois', {
        pathParams: { providerId: String(provider.id) },
        signal,
      }),
      getData<OptionType[]>('aoisOptionsByProviderCategory', {
        pathParams: { providerCategoryId: String(categoryId) },
        transformResponse: specialtiesToOptions,
        signal,
      }),
    ]);
    const matches = (Array.isArray(aois) ? aois : []).flatMap((aoi) => {
      const specialty = specialtyOptions.find((option) =>
        sameName(option.label, aoi.specialtyName)
      );
      return specialty ? [specialty] : [];
    });
    // Several areas of interest in the same specialty still name one specialty.
    const distinct = [...new Map(matches.map((option) => [option.value, option])).values()];
    if (distinct.length === 1) return { ...defaults, aoiSpecialty: distinct[0] };
  } catch {
    // The category default alone is still useful.
  }
  return defaults;
};
